import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { listProveedores, actualizarProveedor } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import ProveedorFormModal from './ProveedorFormModal';
import '../finanzas/ui.css';
import './Proveedores.css';
import FondoModal from '../common/FondoModal';

// Catálogo de proveedores (solo datos del proveedor). Saldos, vencimientos y pagos están en Cuentas por pagar.
// No se eliminan: se desactivan (conservan compras y deudas; no se pueden usar en compras nuevas).
export default function Proveedores() {
  const { sucursalId } = useSucursalActiva();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [form, setForm] = useState(null);  // null | {} (nuevo) | proveedor
  const [ver, setVer] = useState(null);    // proveedor en consulta

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    setCargando(true);
    try { setLista(await listProveedores(sucursalId)); } catch { setLista([]); } finally { setCargando(false); }
  }, [sucursalId]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return lista.filter((p) => (verInactivos || p.activo) && (!q || [p.nombre, p.nombreComercial, p.nit, p.contacto, p.telefono].some((x) => String(x || '').toLowerCase().includes(q))));
  }, [lista, buscar, verInactivos]);

  const cambiarEstado = async (p) => {
    const desactivar = p.activo;
    const ok = await Swal.fire({ title: desactivar ? 'Desactivar proveedor' : 'Reactivar proveedor', icon: 'question', showCancelButton: true,
      text: desactivar ? `${p.nombre} no podrá usarse en compras nuevas. Sus compras y cuentas por pagar se conservan y se pueden terminar de pagar.`
        : `${p.nombre} vuelve a estar disponible para compras.`, confirmButtonText: desactivar ? 'Desactivar' : 'Reactivar', cancelButtonText: 'Cancelar' });
    if (!ok.isConfirmed) return;
    try { await actualizarProveedor(p.id, { ...p, activo: !p.activo }); await cargar(); }
    catch (e) { Swal.fire('No se pudo actualizar', e.message || '', 'error'); }
  };

  return (
    <div className="prov-shell">
      <header className="prov-header">
        <div><h1>Proveedores</h1><p style={{ margin: 0, color: 'var(--ink-muted)' }}>Catálogo de proveedores. Saldos y pagos: <Link to="/Finanzas/CuentasPorPagar">Cuentas por pagar</Link>.</p></div>
        <div className="prov-actions"><button className="fx-btn fx-btn--primary" onClick={() => setForm({})}>+ Crear proveedor</button></div>
      </header>
      <div className="prov-filtros">
        <input type="search" placeholder="Buscar por nombre, NIT, contacto o teléfono" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar proveedor" />
        <label><input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} /> Mostrar desactivados</label>
      </div>
      <div className="fx-card">
        {cargando ? <p className="fx-empty">Cargando…</p> : visibles.length ? (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Proveedor</th><th>NIT</th><th>Contacto</th><th>Teléfono</th><th>Estado</th><th>Acciones</th></tr></thead>
            <tbody>{visibles.map((p) => (
              <tr key={p.id} className={p.activo ? '' : 'prov-inactivo'}>
                <td data-label="Proveedor"><b>{p.nombre}</b>{p.nombreComercial && <small className="prov-sub">{p.nombreComercial}</small>}</td>
                <td data-label="NIT">{p.nit || '—'}</td>
                <td data-label="Contacto">{p.contacto || '—'}</td>
                <td data-label="Teléfono">{p.telefono || '—'}</td>
                <td data-label="Estado"><span className={`fx-badge fx-badge--${p.activo ? 'completado' : 'proximo'}`}>{p.activo ? 'Activo' : 'Desactivado'}</span></td>
                <td data-label="Acciones"><div className="prov-row-actions">
                  <button className="fx-link" onClick={() => setVer(p)}>Ver</button>
                  <button className="fx-link" onClick={() => setForm(p)}>Editar</button>
                  <button className="fx-link" style={p.activo ? { color: 'var(--danger)' } : undefined} onClick={() => cambiarEstado(p)}>{p.activo ? 'Desactivar' : 'Reactivar'}</button>
                </div></td>
              </tr>))}
            </tbody>
          </table>
        ) : <p className="fx-empty">{lista.length ? 'Sin resultados.' : 'Aún no hay proveedores. Crea el primero con “+ Crear proveedor”.'}</p>}
      </div>

      {ver && <FondoModal onClose={() => setVer(null)}>
        <section className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Proveedor" onMouseDown={(e) => e.stopPropagation()}>
          <header><div><h2>{ver.nombre}</h2>{ver.nombreComercial && <p>{ver.nombreComercial}</p>}</div><button type="button" aria-label="Cerrar" onClick={() => setVer(null)}>×</button></header>
          <dl className="cp-datos">
            <div><dt>NIT</dt><dd>{ver.nit || '—'}</dd></div><div><dt>Contacto</dt><dd>{ver.contacto || '—'}</dd></div>
            <div><dt>Teléfono</dt><dd>{ver.telefono || '—'}</dd></div><div><dt>Dirección</dt><dd>{ver.direccion || '—'}</dd></div>
            <div><dt>Días de crédito (por defecto)</dt><dd>{ver.diasCredito}</dd></div>
            <div><dt>Estado</dt><dd>{ver.activo ? 'Activo' : 'Desactivado'}</dd></div>
            {ver.observaciones && <div><dt>Observaciones</dt><dd>{ver.observaciones}</dd></div>}
          </dl>
          <div className="fx-actions"><Link className="fx-btn" to="/Finanzas/CuentasPorPagar">Ver cuentas por pagar</Link>
            <button type="button" className="fx-btn fx-btn--primary" onClick={() => { setForm(ver); setVer(null); }}>Editar</button></div>
        </section>
      </FondoModal>}
      {form && <ProveedorFormModal proveedor={form.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); cargar(); }} />}
    </div>
  );
}
